import { useState } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Eye, EyeOff, ShieldCheck } from 'lucide-react';

export default function AlterarSenhaPage() {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.current_password || !form.new_password || !form.confirm_password) {
      toast.error('Preencha todos os campos');
      return;
    }

    if (form.new_password.length < 6) {
      toast.error('A nova senha deve ter no minimo 6 caracteres');
      return;
    }

    if (form.new_password !== form.confirm_password) {
      toast.error('Nova senha e confirmacao nao conferem');
      return;
    }

    setSaving(true);
    try {
      await api.post('/auth/change-password', form);
      toast.success('Senha alterada com sucesso!');
      setForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto" data-testid="alterar-senha-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'color-mix(in srgb, var(--cor-primaria, #007AFF) 15%, transparent)' }}>
          <Lock className="w-5 h-5" style={{ color: 'var(--cor-primaria, #007AFF)' }} />
        </div>
        <div>
          <h1 className="text-xl font-bold text-white">Alterar Senha</h1>
          <p className="text-sm text-zinc-400">Atualize sua senha de acesso</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5 bg-[#141414] border border-[#27272A] rounded-xl p-6">
        <div className="space-y-2">
          <Label className="text-zinc-300 text-sm">Senha Atual</Label>
          <div className="relative">
            <Input
              type={showCurrent ? 'text' : 'password'}
              value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })}
              placeholder="Digite sua senha atual"
              className="bg-[#0A0A0A] border-[#27272A] text-white pr-10"
              data-testid="input-current-password"
            />
            <button
              type="button"
              onClick={() => setShowCurrent(!showCurrent)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
              data-testid="toggle-current-password"
            >
              {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-zinc-300 text-sm">Nova Senha</Label>
          <div className="relative">
            <Input
              type={showNew ? 'text' : 'password'}
              value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              placeholder="Minimo 6 caracteres"
              className="bg-[#0A0A0A] border-[#27272A] text-white pr-10"
              data-testid="input-new-password"
            />
            <button
              type="button"
              onClick={() => setShowNew(!showNew)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
              data-testid="toggle-new-password"
            >
              {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {form.new_password && form.new_password.length < 6 && (
            <p className="text-xs text-red-400" data-testid="password-length-error">Minimo 6 caracteres</p>
          )}
        </div>

        <div className="space-y-2">
          <Label className="text-zinc-300 text-sm">Confirmar Nova Senha</Label>
          <div className="relative">
            <Input
              type={showConfirm ? 'text' : 'password'}
              value={form.confirm_password}
              onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
              placeholder="Repita a nova senha"
              className="bg-[#0A0A0A] border-[#27272A] text-white pr-10"
              data-testid="input-confirm-password"
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
              data-testid="toggle-confirm-password"
            >
              {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {form.confirm_password && form.new_password !== form.confirm_password && (
            <p className="text-xs text-red-400" data-testid="password-mismatch-error">As senhas nao conferem</p>
          )}
        </div>

        <div className="pt-2">
          <Button
            type="submit"
            disabled={saving}
            className="w-full font-semibold"
            style={{ backgroundColor: 'var(--cor-primaria, #007AFF)' }}
            data-testid="submit-change-password"
          >
            <ShieldCheck className="w-4 h-4 mr-2" />
            {saving ? 'Alterando...' : 'Alterar Senha'}
          </Button>
        </div>

        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4 mt-4">
          <p className="text-xs text-zinc-500 leading-relaxed">
            <strong className="text-zinc-400">Dicas de seguranca:</strong><br />
            - Use no minimo 6 caracteres<br />
            - Combine letras, numeros e simbolos<br />
            - Nao reutilize senhas anteriores
          </p>
        </div>
      </form>
    </div>
  );
}
